import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full rounded-lg border border-[#EBDDC6] bg-[#FDF6EC] px-4 py-3 text-sm text-[#3B2A1E] placeholder-[#A08B74] outline-none transition-colors duration-300 focus:border-[#E07A2B]";

const Reservation = () => {
  const { t } = useLang();
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", date: "", time: "19:00", guests: 2, seating: "causerie", notes: "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/reservations`, { ...form, guests: Number(form.guests), email: form.email || null, notes: form.notes || null });
      toast.success(t.reservation.success);
      setForm({ name: "", phone: "", email: "", date: "", time: "19:00", guests: 2, seating: "causerie", notes: "" });
    } catch {
      toast.error(t.reservation.error);
    } finally {
      setSending(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <section id="reservation" className="grain relative border-t border-[#EBDDC6]/70 bg-[#FFFBF3]" data-testid="reservation-section">
      <div className="mx-auto max-w-4xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="mb-10 text-center"
        >
          <p className="mb-3 font-mono2 text-xs uppercase tracking-[0.3em] text-[#A9761F]">{t.reservation.overline}</p>
          <h2 className="font-display text-3xl font-semibold tracking-tight text-[#C25E18] sm:text-4xl lg:text-5xl" data-testid="reservation-title">
            {t.reservation.title}
          </h2>
          <p className="mt-4 text-base text-[#7A6753]">{t.reservation.subtitle}</p>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="rounded-2xl border border-[#EBDDC6] bg-[#F5E9D7] p-6 shadow-[0_24px_60px_rgba(93,64,36,0.15)] sm:p-10"
          data-testid="reservation-form"
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-[#A08B74]">{t.reservation.name} *</label>
              <input required value={form.name} onChange={set("name")} className={inputCls} data-testid="reservation-name-input" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-[#A08B74]">{t.reservation.phone} *</label>
              <input required type="tel" value={form.phone} onChange={set("phone")} placeholder="+32 ..." className={inputCls} data-testid="reservation-phone-input" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-[#A08B74]">{t.reservation.email}</label>
              <input type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="reservation-email-input" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-[#A08B74]">{t.reservation.guests} *</label>
              <select value={form.guests} onChange={set("guests")} className={inputCls} data-testid="reservation-guests-select">
                {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>{n} {n === 1 ? t.reservation.guest : t.reservation.guestsPlural}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-[#A08B74]">{t.reservation.date} *</label>
              <input required type="date" min={today} value={form.date} onChange={set("date")} className={inputCls} data-testid="reservation-date-input" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-[#A08B74]">{t.reservation.time} *</label>
              <input required type="time" value={form.time} onChange={set("time")} className={inputCls} data-testid="reservation-time-input" />
            </div>
          </div>

          <div className="mt-5">
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-[#A08B74]">{t.reservation.seating}</label>
            <div className="flex flex-wrap gap-2" data-testid="reservation-seating-options">
              {[["causerie", t.reservation.seatingCauserie], ["interieur", t.reservation.seatingInterieur]].map(([val, label]) => (
                <button type="button" key={val} onClick={() => setForm((f) => ({ ...f, seating: val }))} data-testid={`reservation-seating-${val}`}
                  className={`rounded-full border px-4 py-2 text-sm transition-all duration-300 ${form.seating === val
                    ? "border-[#E07A2B] bg-[#E07A2B]/15 text-[#E07A2B]"
                    : "border-[#EBDDC6] text-[#7A6753] hover:border-[#E07A2B]/50"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-5">
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-[#A08B74]">{t.reservation.notes}</label>
            <textarea rows={3} value={form.notes} onChange={set("notes")} className={`${inputCls} resize-none`} data-testid="reservation-notes-input" />
          </div>

          <button type="submit" disabled={sending} data-testid="reservation-submit-button"
            className="mt-8 w-full rounded-full bg-[#E07A2B] py-4 text-sm font-semibold text-[#3B2A1E] transition-all duration-300 hover:bg-[#E89B4E] hover:shadow-[0_0_32px_rgba(224,122,43,0.35)] disabled:opacity-60">
            {sending ? t.reservation.sending : t.reservation.submit}
          </button>
        </motion.form>
      </div>
    </section>
  );
};

export default Reservation;
